import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ConceptTree from '../CalculationResults/ConceptTree';

interface OperationalTreePanelProps {
    tree: { items: Record<string, any>; rootId: string };
    selectedItemId: string | null;
    onSelectItem: (id: string) => void;
    searchString: string;
    onSearchChange: (s: string) => void;
    onDragEnd: (result: any) => void;
}

export default function OperationalTreePanel({
    tree,
    selectedItemId,
    onSelectItem,
    searchString,
    onSearchChange,
    onDragEnd,
}: OperationalTreePanelProps) {
    const rootItem = tree.items[tree.rootId];
    const rootChildrenIds: string[] = rootItem?.childrens || [];

    return (
        <div className="flex flex-col h-full">
            {/* Search */}
            <div className="px-3 py-2 border-b border-slate-200">
                <div className="relative">
                    <span className="material-symbols-outlined text-[18px] text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2">
                        search
                    </span>
                    <input
                        type="text"
                        value={searchString}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search items..."
                        className="w-full pl-8 pr-8 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white"
                    />
                    {searchString && (
                        <button
                            onClick={() => onSearchChange('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                            <span className="material-symbols-outlined text-[16px]">close</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Tree with drag & drop */}
            <div className="flex-1 overflow-y-auto py-1">
                {rootChildrenIds.length > 0 ? (
                    searchString ? (
                        // When searching, disable drag & drop for clarity
                        <ConceptTree
                            itemIds={rootChildrenIds}
                            tree={tree}
                            selectedItemId={selectedItemId}
                            onSelectItem={onSelectItem}
                            level={0}
                            searchString={searchString}
                        />
                    ) : (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="operational-tree" type="root">
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                        {rootChildrenIds.map((id, index) => (
                                            <Draggable key={id} draggableId={id} index={index}>
                                                {(provided, _snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={{
                                                            ...provided.draggableProps.style,
                                                            userSelect: 'none',
                                                        }}
                                                    >
                                                        <ConceptTree
                                                            itemIds={[id]}
                                                            tree={tree}
                                                            selectedItemId={selectedItemId}
                                                            onSelectItem={onSelectItem}
                                                            level={0}
                                                            searchString=""
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )
                ) : (
                    <div className="px-4 py-8 text-center text-sm text-slate-400">
                        No items available
                    </div>
                )}
            </div>
        </div>
    );
}
